import { useMemo } from "react"
import { type OperationContext, type OperationResult, useMutation, useQuery } from "urql"

import type { Company, CompanyGetSuccess, CompanyMutationUpdateArgs, Mutation, Query } from "@/graphql/codegen/graphql"
import { CompanyInfoDocument, UpdateNewCompanyMutationDocument } from "@/graphql/codegen/graphql"

interface UseCompanyInfoProps {
  companyId: string
}

interface UseCompanyInfoReturn {
  companyInfo: Company
  fetching: boolean
  fetchCompanyInfo: (opts?: Partial<OperationContext>) => void
  updateCompany: (
    variables: CompanyMutationUpdateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "company">, CompanyMutationUpdateArgs>>
}

const useCompanyInfo = ({ companyId }: UseCompanyInfoProps): UseCompanyInfoReturn => {
  const [{ data, fetching }, fetchCompanyInfo] = useQuery<Pick<Query, "company">>({
    query: CompanyInfoDocument,
    variables: { companyId },
  })

  const companyInfo = useMemo(() => (data?.company?.get as CompanyGetSuccess)?.company, [data])

  // Mutation to update a company info
  const [, updateCompany] = useMutation<Pick<Mutation, "company">, CompanyMutationUpdateArgs>(
    UpdateNewCompanyMutationDocument
  )

  return {
    companyInfo,
    fetching,
    fetchCompanyInfo,
    updateCompany,
  }
}

export default useCompanyInfo
